import { createSearchParams, Link } from 'react-router';
import React, { useState } from 'react';
import Table from '../../table/Table';
import {
  getAttributeClass,
  getDisplayName,
  getDisplayValue,
  getOrderedSchemaFieldsOfSearchResults,
} from '../../schema/helper';
import { ApiSortDirection } from '../../api';

import './TransactionsTable.scss';
import { Col, Collapse, Row } from 'react-bootstrap';
import ShowHideButton from '../ShowHideButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { snakeToCamelCase } from '../helper';
import {
  InternalApiAttributeType,
  InternalApiEntitySchemaField,
  InternalApiEntityType,
  InternalApiSearchOperator,
  InternalApiTransactionsPage,
} from '../../api-internal';
import { useQueryEntitySchema } from '../../schema/entitySchemaActions';
import { getPersonPath, getTransactionPath, InternalNavigation } from '../../navigation/helper';
import { SearchParamKeys } from '../searchParamKeys';
import DataLoadingErrorFallback from '../errors/DataLoadingErrorFallback';
import { SalvProgressCircle } from '@salvapp/ui-components';
import { getSchemaFieldsOfSearchResults, isSortable } from '../../search/helper';
import { WarnTooltip } from '../tooltips/WarnTooltip';

export interface TransactionsTableProps {
  page: InternalApiTransactionsPage;
  fetching: boolean;
  setFetching: (value: boolean) => void;
  sortByAttribute: (attribute: string, direction: ApiSortDirection, query: string | undefined) => void;
  onSearch?: (filter: string | undefined) => void;
  fetchPage: (page: number) => void;
  toCustomer?: boolean;
  actions?: () => React.ReactNode;
}

export const formatAmount = (
  transactionSchemaField: InternalApiEntitySchemaField | undefined,
  isIncoming = true,
  value: string | undefined
) => {
  if (!value) return '-';

  const amount = getDisplayValue(transactionSchemaField, value);
  if (isIncoming) {
    return `+${amount}`;
  } else if (amount.slice(0, 1) === '-') {
    return amount;
  } else {
    return `-${amount}`;
  }
};

export const TransactionsTable = ({
  page,
  fetching,
  sortByAttribute,
  onSearch,
  fetchPage,
  toCustomer = true,
  actions,
}: TransactionsTableProps): React.JSX.Element | null => {
  const [expanded, setExpanded] = useState<string[]>([]);

  const {
    data: schema,
    isPending: isSchemaPending,
    isError: isSchemaError,
  } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);

  if (!page.content) {
    return null;
  }

  if (isSchemaError) {
    return <DataLoadingErrorFallback message={'Could not load Schema'} />;
  } else if (isSchemaPending) {
    return <SalvProgressCircle ariaLabel={'Loading Schema'} />;
  }

  const { schemaFieldsOfSearchResults, fieldNamesWithMissingSchema } = getSchemaFieldsOfSearchResults(
    page,
    schema.schemaFields
  );

  const expand = (id: string): void => {
    if (expanded.includes(id)) {
      setExpanded(expanded.filter((item) => item !== id));
      return;
    }
    setExpanded([...expanded, id]);
  };

  const getAmountClass = (key: string, isIncoming = true) => {
    if (key === 'amount') {
      return isIncoming ? '' : 'amount-outgoing';
    }
    return '';
  };

  const getStatusClass = (value: string) => {
    if (value === 'Rejected') return 'rejected';
    else if (value === 'Cancelled') return 'cancelled';
    return '';
  };

  const renderMainValues = (
    isIncoming: boolean | undefined,
    key: string,
    value: any,
    schemaField: InternalApiEntitySchemaField | undefined
  ) => {
    switch (key) {
      case 'status': {
        return <span className={getStatusClass(value)}>{getDisplayValue(schemaField, value)}</span>;
      }
      case 'amount': {
        return <span className={getAmountClass(key, isIncoming)}>{formatAmount(schemaField, isIncoming, value)}</span>;
      }
      default: {
        return getDisplayValue(schemaField, value);
      }
    }
  };

  const renderCounterparty = (attributes: any, isIncoming: boolean | undefined): React.JSX.Element => {
    if (isIncoming !== undefined && isIncoming && attributes) {
      return (
        <div className='counterparty'>
          <span className='name'>{attributes['sender_name'] || attributes[snakeToCamelCase('sender_name')]}</span>
          <span className='account'>
            {attributes['sender_account'] || attributes[snakeToCamelCase('sender_account')]}
          </span>
        </div>
      );
    } else if (isIncoming !== undefined && !isIncoming && attributes) {
      return (
        <div className='counterparty'>
          <span className='name'>{attributes['receiver_name'] || attributes[snakeToCamelCase('receiver_name')]}</span>

          <span className='account'>
            {attributes['receiver_account'] || attributes[snakeToCamelCase('receiver_account')]}
          </span>
        </div>
      );
    } else {
      return <>-</>;
    }
  };

  return (
    <Table
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>Transactions</span>
          {fieldNamesWithMissingSchema.length ? (
            <WarnTooltip
              tooltip={`Fields ${fieldNamesWithMissingSchema} exist in Search results but are not specified in your Schema. You should stop seeing this warning when Transaction re-indexing has finished.`}
            />
          ) : undefined}
        </div>
      }
      total={page.total}
      page={page.number}
      size={page.size}
      items={page.content}
      fetchPage={fetchPage}
      onSearch={onSearch}
      actions={actions}
      attributes={[
        {
          key: 'Details',
          sortable: false,
        },
        {
          key: 'Counterparty',
          sortable: false,
        },
        ...schemaFieldsOfSearchResults.map((schemaField) => ({
          key: schemaField.fieldName,
          sortable:
            schemaField.fieldName !== 'details' && schemaField.fieldName !== 'counterparty' && isSortable(schemaField),
        })),
        {
          key: 'ID',
          sortable: false,
        },
      ]}
      striped={false}
      schema={schema}
      onSort={sortByAttribute}
      fetching={fetching}
      body={(transactions) =>
        transactions.length > 0 &&
        transactions.map((t) => {
          const isIncoming = t.attributes && t.attributes['direction'] === 'I';
          return (
            <React.Fragment key={t.id}>
              <tr>
                <td key={'details'} className={'d-flex action-btns'}>
                  <div onClick={() => expand(t.id)}>
                    <ShowHideButton expanded={expanded.includes(t.id)} withText={false} />
                  </div>
                  <Link data-testid='transaction-link' className='btn btn-link text-end' to={getTransactionPath(t.id)}>
                    <FontAwesomeIcon icon={faFileLines} />
                  </Link>
                </td>
                <td key={'counterparty'}>{renderCounterparty(t.attributes, isIncoming)}</td>
                {schemaFieldsOfSearchResults.map((schemaField) => (
                  <td key={schemaField.fieldName} className={`${getAttributeClass(schemaField)}`}>
                    {t.attributes &&
                      renderMainValues(
                        isIncoming,
                        schemaField.fieldName,
                        t.attributes[schemaField.fieldName],
                        schemaField
                      )}
                  </td>
                ))}
                <td key={t.id}>{t.id}</td>
              </tr>
              <tr className={`expanded-details ${expanded.includes(t.id) ? 'showing' : 'hidden'}`}>
                <td colSpan={schemaFieldsOfSearchResults.length}>
                  <Collapse in={expanded.includes(t.id)}>
                    <div>
                      <div className='transaction-details'>
                        {t.attributes &&
                          getOrderedSchemaFieldsOfSearchResults(
                            schema?.schemaFields.filter((schemaField) =>
                              Object.keys(t.attributes).some((fieldName) => fieldName === schemaField.fieldName)
                            )
                          )
                            .reduce((acc: string[][], schemaField, i) => {
                              const columnElements =
                                (t.attributes && Math.ceil(Object.keys(t.attributes).length / 3)) || 3;
                              return (
                                i % columnElements
                                  ? acc[acc.length - 1].push(schemaField.fieldName)
                                  : acc.push([schemaField.fieldName]),
                                acc
                              );
                            }, [])
                            .map((column, index) => (
                              <div key={index} className='column'>
                                {column.map((key) => (
                                  <Row key={key}>
                                    <Col className='keys'>
                                      {getDisplayName(
                                        schemaFieldsOfSearchResults.find((schemaField) => schemaField.fieldName === key)
                                      )}
                                    </Col>
                                    <Col className='values'>
                                      <Link
                                        to={
                                          toCustomer
                                            ? getPersonPath(t.personId!)
                                            : {
                                                pathname: InternalNavigation.transactionSearch,
                                                search: createSearchParams({
                                                  [SearchParamKeys.attributeIdentifier]:
                                                    schemaFieldsOfSearchResults.find(
                                                      (schemaField) => schemaField.fieldName === key
                                                    )!.id,
                                                  [SearchParamKeys.attributeType]:
                                                    InternalApiAttributeType.SCHEMA_FIELD,
                                                  [SearchParamKeys.operator]: InternalApiSearchOperator.IS_EXACTLY,
                                                  [SearchParamKeys.query]: t.attributes?.[key] ?? '',
                                                }).toString(),
                                              }
                                        }
                                      >
                                        {t.attributes &&
                                          getDisplayValue(
                                            schemaFieldsOfSearchResults.find(
                                              (schemaField) => schemaField.fieldName === key
                                            ),
                                            t.attributes[key]
                                          )}
                                      </Link>
                                    </Col>
                                  </Row>
                                ))}
                              </div>
                            ))}
                      </div>
                    </div>
                  </Collapse>
                </td>
              </tr>
            </React.Fragment>
          );
        })
      }
    />
  );
};

export default TransactionsTable;
